import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { LatePolicyInfoButton } from '../components/LatePolicy';

// ── 8D Studios day schedule ──────────────────────────────────────────────────
const WEEKDAY_BLOCKS = [
  { start: '10:00', end: '10:15', label: 'Daily Planning',          icon: '📋', color: '#6D28D9', light: '#F5F3FF', note: 'Tasks · Priorities · Client deadlines' },
  { start: '10:15', end: '13:30', label: 'Deep Work Block #1',      icon: '🎨', color: '#1D4ED8', light: '#EFF6FF', note: 'Branding · Logo · Packaging · PPT · Video' },
  { start: '13:30', end: '14:15', label: 'Lunch Break',             icon: '🍽️', color: '#059669', light: '#ECFDF5', note: 'Step away · Rest · Recharge' },
  { start: '14:15', end: '17:15', label: 'Deep Work Block #2',      icon: '⚡', color: '#0369A1', light: '#E0F2FE', note: 'Social media · Revisions · Client deliverables' },
  { start: '17:15', end: '17:30', label: 'Tea Break',               icon: '☕', color: '#92400E', light: '#FFFBEB', note: 'Short break' },
  { start: '17:30', end: '19:00', label: 'Communication & Closure', icon: '📞', color: '#BE185D', light: '#FDF2F8', note: 'Client calls · Reviews · Next day planning' },
];
const SATURDAY_BLOCKS = [
  { start: '10:00', end: '10:15', label: 'Daily Planning',  icon: '📋', color: '#6D28D9', light: '#F5F3FF', note: 'Tasks · Priorities' },
  { start: '10:15', end: '14:00', label: 'Work Session',    icon: '🎨', color: '#1D4ED8', light: '#EFF6FF', note: 'Client work · Deliverables' },
];

function timeToMinutes(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}

function nowMinutes() {
  const n = new Date();
  return n.getHours() * 60 + n.getMinutes();
}

function fmtDuration(minutes) {
  if (!minutes && minutes !== 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function fmtTime(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function getCurrentBlock(blocks) {
  const now = nowMinutes();
  return blocks.findIndex(b => now >= timeToMinutes(b.start) && now < timeToMinutes(b.end));
}

// ── Live Clock ───────────────────────────────────────────────────────────────
function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const dow = time.toLocaleDateString('en-IN', { weekday: 'long' });
  const date = time.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  const clock = time.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  return (
    <div style={{ textAlign: 'center', marginBottom: 24 }}>
      <div style={{ fontSize: 13, color: 'var(--text3)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {dow} · {date}
      </div>
      <div style={{ fontSize: 42, fontWeight: 800, color: 'var(--text1)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, marginTop: 4 }}>
        {clock}
      </div>
    </div>
  );
}

// ── Day Schedule Timeline ────────────────────────────────────────────────────
function DaySchedule({ blocks, session }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const now = nowMinutes();
  const currentIdx = getCurrentBlock(blocks);
  const dayStart = timeToMinutes(blocks[0].start);
  const dayEnd   = timeToMinutes(blocks[blocks.length - 1].end);
  const dayRange = dayEnd - dayStart;

  return (
    <div className="card" style={{ padding: '20px 22px', marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
        Today's Schedule
      </div>

      {blocks.map((block, i) => {
        const isCurrent = i === currentIdx;
        const isDone    = now >= timeToMinutes(block.end);
        const startMin  = timeToMinutes(block.start);
        const endMin    = timeToMinutes(block.end);
        const blockLen  = endMin - startMin;
        const progress  = isCurrent ? Math.min(100, ((now - startMin) / blockLen) * 100) : isDone ? 100 : 0;

        return (
          <div key={i} style={{
            display: 'flex', gap: 14, marginBottom: i < blocks.length - 1 ? 14 : 0,
            opacity: isDone && !isCurrent ? 0.55 : 1,
          }}>
            {/* Time + dot */}
            <div style={{ width: 52, flexShrink: 0, textAlign: 'right', paddingTop: 2 }}>
              <div style={{ fontSize: 11, color: isCurrent ? block.color : 'var(--text3)', fontWeight: isCurrent ? 700 : 400 }}>
                {block.start}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <div style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: isCurrent ? block.color : isDone ? '#94A3B8' : 'var(--border)',
                  border: isCurrent ? `2px solid ${block.color}` : 'none',
                  boxShadow: isCurrent ? `0 0 0 3px ${block.color}30` : 'none',
                  transition: 'all 0.3s',
                }} />
              </div>
            </div>

            {/* Block content */}
            <div style={{
              flex: 1, padding: '10px 14px', borderRadius: 8,
              background: isCurrent ? block.light : isDone ? 'var(--surface2)' : 'var(--surface)',
              border: isCurrent ? `1px solid ${block.color}40` : '1px solid var(--border)',
              transition: 'all 0.3s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? block.color : 'var(--text1)' }}>
                  {block.icon} {block.label}
                </span>
                {isCurrent && (
                  <span style={{ fontSize: 10, background: block.color, color: '#fff', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                    NOW
                  </span>
                )}
                {isDone && !isCurrent && (
                  <span style={{ fontSize: 11, color: '#94A3B8' }}>✓ done</span>
                )}
                {!isDone && !isCurrent && (
                  <span style={{ fontSize: 10, color: 'var(--text3)' }}>{block.start} – {block.end}</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: isCurrent ? block.color + 'CC' : 'var(--text3)', marginBottom: isCurrent ? 8 : 0 }}>
                {block.note}
              </div>
              {isCurrent && (
                <div style={{ height: 4, background: block.color + '25', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: progress + '%', background: block.color, borderRadius: 2, transition: 'width 0.5s ease' }} />
                </div>
              )}
              {isCurrent && (
                <div style={{ fontSize: 10, color: block.color, marginTop: 4 }}>
                  {fmtDuration(endMin - now)} remaining
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Clock Card ───────────────────────────────────────────────────────────────
function ClockCard({ session, onClockIn, onClockOut, loading, dayOff }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!session?.clock_in || session?.clock_out) return;
    const update = () => {
      const diff = Math.floor((Date.now() - new Date(session.clock_in)) / 60000);
      setElapsed(diff);
    };
    update();
    const t = setInterval(update, 10000);
    return () => clearInterval(t);
  }, [session]);

  if (dayOff) {
    return (
      <div className="card" style={{ padding: '28px 24px', textAlign: 'center', marginBottom: 16, background: 'var(--surface2)' }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🏖️</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text2)' }}>Day Off — Rest & Recharge</div>
      </div>
    );
  }

  const isActive = session?.clock_in && !session?.clock_out;
  const isDone   = session?.clock_in && session?.clock_out;

  return (
    <div className="card" style={{ padding: '28px 24px', textAlign: 'center', marginBottom: 16,
      background: isActive ? '#F0FDF4' : isDone ? '#F8FAFC' : 'var(--surface)',
      border: isActive ? '1px solid #86EFAC' : '1px solid var(--border)',
      transition: 'all 0.4s',
    }}>
      {/* Status badge */}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 14px', borderRadius: 20,
        background: isActive ? '#DCFCE7' : isDone ? '#E0F2FE' : '#FFF3CD',
        color: isActive ? '#15803D' : isDone ? '#0369A1' : '#92400E',
        fontSize: 12, fontWeight: 700, marginBottom: 16 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%',
          background: isActive ? '#15803D' : isDone ? '#0369A1' : '#92400E',
          animation: isActive ? 'pulse 1.5s infinite' : 'none' }} />
        {isActive ? 'CLOCKED IN' : isDone ? 'DAY COMPLETE' : 'NOT YET IN'}
      </div>

      {/* Timer / summary */}
      {isActive && (
        <div>
          <div style={{ fontSize: 48, fontWeight: 900, color: '#15803D', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
            {fmtDuration(elapsed)}
          </div>
          <div style={{ fontSize: 13, color: '#166534', marginTop: 4 }}>
            Clocked in at {fmtTime(session.clock_in)}
            {session.is_late && <span style={{ marginLeft: 8, color: '#D97706' }}>· {fmtDuration(session.late_minutes)} late</span>}
          </div>
        </div>
      )}
      {isDone && (
        <div>
          <div style={{ fontSize: 36, fontWeight: 800, color: '#0369A1' }}>{fmtDuration(session.total_minutes)}</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
            {fmtTime(session.clock_in)} → {fmtTime(session.clock_out)}
            {session.is_late && <span style={{ color: '#D97706' }}> · Late by {fmtDuration(session.late_minutes)}</span>}
            {session.early_leave && <span style={{ color: '#EA580C' }}> · Left early</span>}
          </div>
        </div>
      )}
      {!session && (
        <div>
          <div style={{ fontSize: 28, marginBottom: 8 }}>👋</div>
          <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 4 }}>Ready to start your day?</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Work begins at 10:00 AM</div>
        </div>
      )}

      {/* Action button */}
      {!isDone && (
        <button
          onClick={isActive ? onClockOut : onClockIn}
          disabled={loading}
          style={{
            marginTop: 20, padding: '14px 40px', borderRadius: 12, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: 15, fontWeight: 800, letterSpacing: '0.02em',
            background: isActive ? '#DC2626' : '#15803D',
            color: '#fff', opacity: loading ? 0.7 : 1,
            transition: 'all 0.2s', boxShadow: `0 4px 14px ${isActive ? '#DC262640' : '#15803D40'}`,
          }}>
          {loading ? '...' : isActive ? '⏹ Clock Out' : '▶ Clock In'}
        </button>
      )}
    </div>
  );
}

// ── Team Live Panel (owner only) ─────────────────────────────────────────────
function TeamLivePanel() {
  const [, setTick] = useState(0);
  const { data, refetch } = useQuery({
    queryKey: ['team-today'],
    queryFn: () => api.get('/attendance-sessions/team-today').then(r => r.data),
    refetchInterval: 60000,
  });
  useEffect(() => {
    const t = setInterval(() => setTick(n => n+1), 60000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;

  const { team, dayOff, schedule } = data;
  const inNow   = team.filter(t => t.status === 'in');
  const done    = team.filter(t => t.status === 'done');
  const late    = team.filter(t => t.status === 'late');
  const notIn   = team.filter(t => t.status === 'not-in');
  const onBreak = team.filter(t => t.status === 'break');
  const offline = team.filter(t => t.status === 'offline');

  // Calculate extra minutes worked beyond scheduled end
  function extraMinutes(member) {
    if (!schedule || !member.session) return 0;
    const today = new Date().toISOString().slice(0, 10);
    const isSat = new Date().getDay() === 6;
    const endStr = isSat ? schedule.saturday_end : schedule.weekday_end;
    const [eh, em] = endStr.split(':').map(Number);
    const scheduledEnd = new Date(today);
    scheduledEnd.setHours(eh, em, 0, 0);

    // For "done" members — compare clock_out to scheduled end
    if (member.session.clock_out) {
      const clockOut = new Date(member.session.clock_out);
      return Math.max(0, Math.floor((clockOut - scheduledEnd) / 60000));
    }
    // For "in" members still working — compare now to scheduled end
    const now = new Date();
    return Math.max(0, Math.floor((now - scheduledEnd) / 60000));
  }

  return (
    <div className="card" style={{ padding: '20px 22px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Team Status — Live
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
          <span style={{ color: '#15803D', fontWeight: 600 }}>● {inNow.length} in</span>
          {onBreak.length > 0 && <span style={{ color: '#D97706', fontWeight: 600 }}>{onBreak.length} break</span>}
          {late.length > 0 && <span style={{ color: '#D97706', fontWeight: 600 }}>⚠ {late.length} late</span>}
          {offline.length > 0 && <span style={{ color: '#EF4444', fontWeight: 600 }}>⚡ {offline.length} offline</span>}
          <span style={{ color: 'var(--text3)' }}>{done.length} done · {notIn.length} not in</span>
        </div>
      </div>

      {dayOff && <div style={{ textAlign: 'center', color: 'var(--text2)', fontSize: 13, padding: 12 }}>🏖️ Day off for the team</div>}

      {!dayOff && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {team.map(member => {
            const colors = {
              in:       { bg: '#F0FDF4', dot: '#15803D', text: '#15803D' },
              done:     { bg: '#EFF6FF', dot: '#0369A1', text: '#0369A1' },
              late:     { bg: '#FFFBEB', dot: '#D97706', text: '#D97706' },
              'not-in': { bg: 'var(--surface2)', dot: '#94A3B8', text: 'var(--text3)' },
              break:    { bg: '#FFFBEB', dot: '#D97706', text: '#D97706' },
              offline:  { bg: '#FEF2F2', dot: '#EF4444', text: '#DC2626' },
            };
            const c    = colors[member.status] || colors['not-in'];
            const extra = extraMinutes(member);
            return (
              <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: c.bg, flexWrap: 'wrap' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, flexShrink: 0,
                  animation: member.status === 'in' ? 'pulse 1.5s infinite' : 'none' }} />
                <div style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{member.full_name}</div>
                <div style={{ fontSize: 12, color: c.text, fontWeight: 600 }}>
                  {member.status === 'in'      && `${fmtDuration(member.minutesWorked)} worked`}
                  {member.status === 'done'    && `${fmtDuration(member.minutesWorked)} · done`}
                  {member.status === 'late'    && 'Not in — late'}
                  {member.status === 'not-in'  && 'Not in yet'}
                  {member.status === 'break'   && (() => {
                    const bt = member.activeBreak?.break_type;
                    const label = bt === 'lunch' ? 'Lunch' : bt === 'tea' ? 'Tea break' : bt === 'casual' ? 'Casual' : 'Break';
                    const elapsed = member.activeBreak?.started_at
                      ? fmtDuration(Math.floor((Date.now() - new Date(member.activeBreak.started_at)) / 60000))
                      : '';
                    return `${label} · ${elapsed}`;
                  })()}
                  {member.status === 'offline' && (() => {
                    const ls = member.session?.last_seen;
                    return ls ? `Offline since ${fmtTime(ls)}` : 'Offline / disconnected';
                  })()}
                </div>
                {/* Late arrival badge */}
                {member.session?.is_late && (
                  <span style={{ fontSize: 10, color: '#D97706', background: '#FEF3C7', padding: '2px 7px', borderRadius: 8, fontWeight: 600 }}>
                    late {fmtDuration(member.session.late_minutes)}
                  </span>
                )}
                {/* Extra time beyond scheduled end */}
                {extra > 0 && (
                  <span style={{ fontSize: 10, color: '#7C3AED', background: '#F5F3FF', padding: '2px 7px', borderRadius: 8, fontWeight: 600 }}>
                    +{fmtDuration(extra)} extra
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Saturday Toggle (owner only) ─────────────────────────────────────────────
function SaturdayToggle() {
  const qc = useQueryClient();
  const { data: schedule } = useQuery({
    queryKey: ['work-schedule'],
    queryFn: () => api.get('/attendance-sessions/schedule').then(r => r.data),
  });
  const mutation = useMutation({
    mutationFn: val => api.patch('/attendance-sessions/schedule', { alt_saturday_off: val }),
    onSuccess: () => { qc.invalidateQueries(['work-schedule']); qc.invalidateQueries(['att-today']); toast.success(schedule?.alt_saturday_off ? '2nd & 4th Saturday now working' : '2nd & 4th Saturday now OFF'); },
    onError: () => toast.error('Failed to update'),
  });
  if (!schedule) return null;
  const isOn = schedule.alt_saturday_off;
  return (
    <div className="card" style={{ padding: '18px 22px', marginBottom: 16, border: `1px solid ${isOn ? '#86EFAC' : 'var(--border)'}`, background: isOn ? '#F0FDF4' : 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: isOn ? '#15803D' : 'var(--text1)' }}>
            2nd & 4th Saturday Off
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
            {isOn ? '✅ Active — alternate Saturdays are holidays' : 'Currently disabled — all Saturdays are working days (10AM–2PM)'}
          </div>
        </div>
        <button
          onClick={() => mutation.mutate(!isOn)}
          disabled={mutation.isPending}
          style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: isOn ? '#DC2626' : '#15803D', color: '#fff',
            fontSize: 12, fontWeight: 700 }}>
          {mutation.isPending ? '...' : isOn ? 'Stop' : 'Implement'}
        </button>
      </div>
    </div>
  );
}

// ── Saturday Hours Toggle (owner only) ───────────────────────────────────────
function SaturdayHoursToggle() {
  const qc = useQueryClient();
  const { data: schedule } = useQuery({
    queryKey: ['work-schedule'],
    queryFn: () => api.get('/attendance-sessions/schedule').then(r => r.data),
  });

  const mutation = useMutation({
    mutationFn: ({ date, is_full_day }) =>
      api.post('/attendance-sessions/saturday-override', { date, is_full_day }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries(['work-schedule']); qc.invalidateQueries(['att-today']); },
    onError: () => toast.error('Failed to update Saturday schedule'),
  });

  if (!schedule) return null;

  // Build list: this Saturday + next 3 Saturdays
  const saturdays = [];
  const d = new Date();
  const dow = d.getDay();
  // days until next Saturday (0 if today is Saturday)
  const daysToSat = dow === 6 ? 0 : (6 - dow);
  for (let i = 0; i < 4; i++) {
    const sat = new Date(d);
    sat.setDate(d.getDate() + daysToSat + i * 7);
    saturdays.push(sat.toISOString().slice(0, 10));
  }

  const overrideDates = (schedule.saturday_full_day_dates || []).map(x =>
    x instanceof Date ? x.toISOString().slice(0, 10) : String(x).slice(0, 10)
  );

  return (
    <div className="card" style={{ padding: '18px 22px', marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Saturday Work Hours</div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
        Default: 10 AM – 2 PM (half day). Mark as full day to extend to {schedule.weekday_end}.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {saturdays.map(dateStr => {
          const isFullDay = overrideDates.includes(dateStr);
          const label = new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
          const isToday = dateStr === new Date().toISOString().slice(0, 10);
          return (
            <div key={dateStr} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: isFullDay ? '#EFF6FF' : 'var(--surface2, #f9f9f9)', border: `1px solid ${isFullDay ? '#BFDBFE' : 'var(--border)'}` }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
                {isToday && <span style={{ fontSize: 10, marginLeft: 6, color: '#7C3AED', background: '#F5F3FF', padding: '1px 6px', borderRadius: 6, fontWeight: 700 }}>TODAY</span>}
                <div style={{ fontSize: 11, color: isFullDay ? '#1D4ED8' : 'var(--text3)', marginTop: 1 }}>
                  {isFullDay ? `Full day · 10 AM – ${schedule.weekday_end}` : 'Half day · 10 AM – 2 PM'}
                </div>
              </div>
              <button
                onClick={() => mutation.mutate({ date: dateStr, is_full_day: !isFullDay })}
                disabled={mutation.isPending}
                style={{ padding: '6px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                  background: isFullDay ? '#DC2626' : '#1D4ED8', color: '#fff' }}>
                {mutation.isPending ? '…' : isFullDay ? 'Set Half Day' : 'Set Full Day'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── My History ───────────────────────────────────────────────────────────────
function MyHistory() {
  const { user: histUser } = useAuth();
  const { data = [] } = useQuery({
    queryKey: ['my-attendance', histUser?.id],
    queryFn: () => api.get('/attendance-sessions/my-history?days=14').then(r => r.data),
    enabled: !!histUser,
  });
  if (!data.length) return null;
  return (
    <div className="card" style={{ padding: '16px 20px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
        Last 14 Days
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {data.slice(0, 10).map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
            <div style={{ width: 80, color: 'var(--text3)', flexShrink: 0 }}>
              {new Date(s.session_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
            </div>
            <div style={{ flex: 1, color: 'var(--text2)' }}>
              {fmtTime(s.clock_in)} → {(() => {
                if (s.clock_out) return fmtTime(s.clock_out);
                const isToday = new Date(s.session_date).toISOString().slice(0,10) === new Date().toISOString().slice(0,10);
                if (isToday) return <span style={{ color: '#D97706' }}>still in</span>;
                return <span style={{ color: '#DC2626' }}>missed checkout</span>;
              })()}
            </div>
            <div style={{ fontWeight: 600, color: s.total_minutes >= 480 ? '#15803D' : 'var(--text2)' }}>
              {fmtDuration(s.total_minutes)}
            </div>
            {s.is_late && <span style={{ fontSize: 10, color: '#D97706', background: '#FEF3C7', padding: '1px 5px', borderRadius: 6 }}>late</span>}
            {s.early_leave && <span style={{ fontSize: 10, color: '#EA580C', background: '#FFF7ED', padding: '1px 5px', borderRadius: 6 }}>early out</span>}
            {!s.clock_out && new Date(s.session_date).toISOString().slice(0,10) !== new Date().toISOString().slice(0,10) && (
              <span style={{ fontSize: 10, color: '#DC2626', background: '#FEF2F2', padding: '1px 5px', borderRadius: 6 }}>no checkout</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function AttendanceClockPage() {
  const { isOwner, isAdmin, user } = useAuth();
  const qc = useQueryClient();
  const [clockLoading, setClockLoading] = useState(false);
  const [showClockOutConfirm, setShowClockOutConfirm] = useState(false);

  const { data: todayData, refetch } = useQuery({
    queryKey: ['attendance-today', user?.id],
    queryFn: () => api.get('/attendance-sessions/today').then(r => r.data),
    refetchInterval: 60000,
    enabled: !!user,
  });

  const session  = todayData?.session || null;
  const dayOff   = todayData?.dayOff || false;
  const today    = new Date();
  const isSat    = today.getDay() === 6;
  const blocks   = isSat ? SATURDAY_BLOCKS : WEEKDAY_BLOCKS;

  async function handleClockIn() {
    if (clockLoading) return;
    setClockLoading(true);
    try {
      await api.post('/attendance-sessions/clock-in');
      await refetch();
      qc.invalidateQueries(['team-today']);
      toast.success('Clocked in! Have a productive day 🚀');
    } catch(e) {
      if (e.response?.status === 409) {
        await refetch();
        toast.success('Already clocked in!');
      } else {
        toast.error(e.response?.data?.error || 'Failed to clock in');
      }
    } finally { setClockLoading(false); }
  }

  async function handleClockOut() {
    setClockLoading(true);
    try {
      const r = await api.post('/attendance-sessions/clock-out');
      await refetch();
      qc.invalidateQueries(['team-today']);
      const mins = r.data.session?.total_minutes;
      toast.success(`Clocked out. Great work — ${fmtDuration(mins)} today! 👏`);
    } catch(e) {
      toast.error(e.response?.data?.error || 'Failed to clock out');
    } finally { setClockLoading(false); }
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div className="section-head">
        <h2>Attendance {!isOwner && <LatePolicyInfoButton />}</h2>
      </div>

      {/* Owner-only features */}
      {isOwner && <SaturdayToggle />}
      {isOwner && <SaturdayHoursToggle />}
      {isOwner && <TeamLivePanel />}

      {/* Live Clock */}
      <LiveClock />

      {/* Clock in/out card */}
      <ClockCard
        session={session}
        onClockIn={handleClockIn}
        onClockOut={() => setShowClockOutConfirm(true)}
        loading={clockLoading}
        dayOff={dayOff}
      />

      {/* Clock-out confirmation dialog */}
      {showClockOutConfirm && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)', zIndex:9998, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:16, padding:'32px 36px', maxWidth:380, width:'90vw', boxShadow:'0 20px 60px rgba(0,0,0,0.25)', textAlign:'center' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>⏹</div>
            <div style={{ fontSize:17, fontWeight:700, color:'#111', marginBottom:8 }}>Clock out now?</div>
            <div style={{ fontSize:13, color:'var(--text2)', marginBottom:24 }}>Make sure you've updated your work log before leaving.</div>
            <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
              <button onClick={() => { setShowClockOutConfirm(false); handleClockOut(); }}
                style={{ background:'#DC2626', color:'#fff', border:'none', borderRadius:8, padding:'10px 24px', fontSize:14, fontWeight:700, cursor:'pointer' }}>
                Yes, Clock Out
              </button>
              <button onClick={() => setShowClockOutConfirm(false)}
                style={{ background:'#f5f5f5', color:'#555', border:'1px solid #e0e0e0', borderRadius:8, padding:'10px 24px', fontSize:14, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day schedule — only if not day off */}
      {!dayOff && <DaySchedule blocks={blocks} session={session} />}

      {/* Personal history */}
      <MyHistory />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}
